/**
 * The globals helper reads global variables, that were loaded before the app start:
 * main.ts injects the globals and session info loaded from the backend,
 * which then are globally available through the helper functions.
 *
 * This file is part of Ebau
 * (c) Kaden und Partner AG
 */

import { Adresse } from '@/models/Adresse';
import { Benutzer } from '@/models/Benutzer';
import { createModel } from '@kp/js-model'
import { reactive } from 'vue';

export interface AppGlobals {
    session: boolean;
    appVersion: string,
    webroot: string,
    loading: boolean,
    loadingCounter: number,
    snackbarContent: string,
    redirectUrl: string,
    customer: string,
    config: { gueltigkeit_zusage_deadline: number, env?: string } | null,
}

export interface AppSession {
    benutzer: Benutzer | null;
    csrfToken: string;
}
/** reactive singleton object with global variables, injected on app startup */
const globals = reactive<AppGlobals>({} as AppGlobals);
/** reactive singleton object with session variables, injected on app startup */
const session = reactive<AppSession>({} as AppSession);

/**
 * Takes a raw object with app globals, and sets them in the globals
 * reactive object. Should be set by the app init process, when
 * the globals are loaded from the backend.
 *
 * @param newGlobals
 */
export function setGlobals(newGlobals: AppGlobals) {
    Object.assign(globals, newGlobals);
}

/**
 * Returns a reactive object with the global variables
 */
export function useGlobals(): AppGlobals {
    return globals;
}

/**
 * Takes a raw object with app session data, and sets them in the session
 * reactive object. Should be set by the app init process, when
 * the session data are loaded from the backend.
 *
 * @param newSession
 */
export function setSession(newSession: AppSession) {
    session.benutzer = newSession.benutzer ? new Benutzer().set(newSession.benutzer) : null;
    session?.benutzer?.commit();
    session.csrfToken = newSession.csrfToken ?? null;
}

export async function udpateProfilAdresse() {
    if (session.benutzer) {
        const profilId = session.benutzer.profilAdresse?.id;
        if (!profilId) {
            return;
        }
        const adresse = createModel(Adresse, { id: profilId });
        await adresse.load();
        session.benutzer.profilAdresse = adresse;
        session.benutzer.commit();
    }
}

/**
 * Returns a reactive object with the session variables
 */
export function useSession(): AppSession {
    return session as AppSession;
}

/**
 * Returns a global variable value from the globals object delivered by auth/init
 * @param {string} key The global var name
 * @returns the value or null
 */
export function globalVar(key: keyof AppGlobals) {
    return useGlobals()[key];
}

/**
 * Returns the authenticated benutzer, or null
 */
export function useAuthUser(): Benutzer | null {
    return session.benutzer as Benutzer | null;
}

export function isAuthenticated(): boolean {
    return useAuthUser() !== null;
}

export function isAdmin(): boolean {
    return useAuthUser()?.rolle === 'admin';
}
